/**
 * La app de riders todavía no está lanzada: el modo "Repartidores independientes"
 * se oculta en la UI. Si una sucursal ya lo tiene prendido, el toggle sigue
 * visible para poder apagarlo. Cuando riders salga, volver a true o migrar a capability.
 */
export const VUELTO_RIDERS_AVAILABLE = false;

export type TimeShift = { open: string; close: string };

export type BusinessHourDay = {
  dayOfWeek: number; // 0=domingo ... 6=sábado
  isOpen: boolean;
  shifts: TimeShift[];
};

export type ManualOverride = 'auto' | 'force_open' | 'force_close';
export type StoreTheme = 'light' | 'cream' | 'rose' | 'dark';

export const STORE_THEMES: readonly StoreTheme[] = [
  'light',
  'cream',
  'rose',
  'dark',
] as const;

export function normalizeStoreTheme(raw: unknown): StoreTheme {
  const s = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if ((STORE_THEMES as readonly string[]).includes(s)) return s as StoreTheme;
  // Temas oscuros eliminados (midnight/forest/burgundy) → dark.
  if (s === 'midnight' || s === 'forest' || s === 'burgundy') return 'dark';
  return 'light';
}

export function isDarkStoreTheme(theme: StoreTheme | null | undefined): boolean {
  return theme === 'dark';
}

export type StoreProfile = {
  branchId: string;
  slug: string;
  /** Nombre público de la tienda online; null = fallback canónico Org · Sucursal. */
  displayName: string | null;
  logoUrl: string | null;
  coverStoragePath: string | null;
  /** Fondo opcional full-bleed de la tienda (portal muestra con scrim). */
  backgroundStoragePath: string | null;
  /** Tema visual de la tienda online ('light' | 'dark'). */
  storeTheme: StoreTheme;
  description: string | null;
  phone: string | null;
  whatsapp: string | null;
  orderContactMessage: boolean;
  orderContactWhatsapp: boolean;
  orderContactPhone: boolean;
  isPublic: boolean;
  /** Legacy OR of own + riders */
  deliveryEnabled: boolean;
  ownDeliveryEnabled: boolean;
  vueltoRidersEnabled: boolean;
  isOnDuty: boolean;
  businessHours: BusinessHourDay[];
  manualOverride: ManualOverride;
  manualOverrideUntil: string | null;
  tcAcceptedAt: string | null;
  /** null = unlimited online open orders */
  maxConcurrentOrders: number | null;
  /** null = no mostrar ETA al aceptar; tipico 10–60 */
  defaultPrepMinutes: number | null;
  /** Opt-in: auto-confirmar pickup ya pago. Default false. */
  autoAcceptPickupPaid: boolean;
  /** Opt-in: aviso al cliente cuando cocina marca listo retiro. Default false. */
  notifyCustomerReadyPickup: boolean;
  /** Monto mínimo de pedido online (retiro + delivery). null = sin mínimo. */
  minOrderAmount: number | null;
  /** Cover focal point 0-100 (50 = center). Controls object-position in portal. */
  coverFocalX: number;
  coverFocalY: number;
  /** Cover zoom 1-4 (1 = sin zoom). Se combina con el focal en el portal. */
  coverZoom: number;
  /** Logo focal 0-100 + zoom 1-4 dentro del frame cuadrado. */
  logoFocalX: number;
  logoFocalY: number;
  logoZoom: number;
  /** Carta presencial /m/<slug> habilitada. Off → 404 y sin banner en la tienda. */
  menuCartaEnabled: boolean;
};

export type StoreProfileDraft = {
  slug: string;
  /** Nombre público de la tienda online; '' = fallback canónico. */
  displayName: string;
  logoUrl: string;
  coverStoragePath: string;
  backgroundStoragePath: string;
  storeTheme: StoreTheme;
  description: string;
  phone: string;
  whatsapp: string;
  orderContactMessage: boolean;
  orderContactWhatsapp: boolean;
  orderContactPhone: boolean;
  isPublic: boolean;
  /** Legacy OR of own + riders */
  deliveryEnabled: boolean;
  ownDeliveryEnabled: boolean;
  vueltoRidersEnabled: boolean;
  isOnDuty: boolean;
  businessHours: BusinessHourDay[];
  manualOverride: ManualOverride;
  manualOverrideUntil: string | null;
  tcAcceptedAt: string | null;
  maxConcurrentOrders: number | null;
  defaultPrepMinutes: number | null;
  autoAcceptPickupPaid: boolean;
  notifyCustomerReadyPickup: boolean;
  minOrderAmount: number | null;
  coverFocalX: number;
  coverFocalY: number;
  coverZoom: number;
  logoFocalX: number;
  logoFocalY: number;
  logoZoom: number;
  menuCartaEnabled: boolean;
};

export type ScheduledClosure = {
  id: string;
  startDate: string;
  endDate: string;
  reason: string | null;
};

export const DAY_LABELS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'] as const;

export function defaultBusinessHours(): BusinessHourDay[] {
  return Array.from({ length: 7 }, (_, dayOfWeek) => ({
    dayOfWeek,
    isOpen: dayOfWeek >= 1 && dayOfWeek <= 6,
    shifts: dayOfWeek >= 1 && dayOfWeek <= 6 ? [{ open: '09:00', close: '21:00' }] : [],
  }));
}

export function emptyStoreProfileDraft(seedSlug = ''): StoreProfileDraft {
  return {
    slug: seedSlug,
    displayName: '',
    logoUrl: '',
    coverStoragePath: '',
    backgroundStoragePath: '',
    storeTheme: 'light',
    description: '',
    phone: '',
    whatsapp: '',
    orderContactMessage: true,
    orderContactWhatsapp: false,
    orderContactPhone: false,
    isPublic: false,
    deliveryEnabled: false,
    ownDeliveryEnabled: false,
    vueltoRidersEnabled: false,
    isOnDuty: false,
    businessHours: defaultBusinessHours(),
    manualOverride: 'auto',
    manualOverrideUntil: null,
    tcAcceptedAt: null,
    maxConcurrentOrders: null,
    defaultPrepMinutes: null,
    autoAcceptPickupPaid: false,
    notifyCustomerReadyPickup: false,
    minOrderAmount: null,
    coverFocalX: 50,
    coverFocalY: 50,
    coverZoom: 1,
    logoFocalX: 50,
    logoFocalY: 50,
    logoZoom: 1,
    menuCartaEnabled: true,
  };
}

export function normalizeSlug(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) && slug.length >= 2 && slug.length <= 64;
}

export function storefrontPublicUrl(portalBaseUrl: string, slug: string): string {
  return `${portalBaseUrl.replace(/\/$/, '')}/${slug}`;
}

/** #904: QR menú presencial — read-only catalog at /m/<slug>. */
export function menuPublicUrl(portalBaseUrl: string, slug: string): string {
  return `${portalBaseUrl.replace(/\/$/, '')}/m/${slug}`;
}

type PortalBusinessHoursEntry = {
  day: number;
  shifts: { open: string; close: string }[];
};

export function parseBusinessHours(value: unknown): BusinessHourDay[] {
  if (!Array.isArray(value)) return defaultBusinessHours();
  const byDay = new Map<number, BusinessHourDay>();
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;

    // Formato nuevo del portal: { day, shifts: [{ open, close }] }
    if ('shifts' in row && Array.isArray(row.shifts)) {
      const dayOfWeek = Number(row.day ?? row.dayOfWeek ?? row.day_of_week);
      if (!Number.isInteger(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6) continue;
      const shifts = (row.shifts as unknown[])
        .filter((s) => s && typeof s === 'object')
        .map((s) => {
          const sh = s as Record<string, unknown>;
          return {
            open: String(sh.open ?? '09:00').slice(0, 5),
            close: String(sh.close ?? '21:00').slice(0, 5),
          };
        });
      const isOpen = shifts.length > 0;
      byDay.set(dayOfWeek, { dayOfWeek, isOpen, shifts });
      continue;
    }

    // Formato legacy de la app: { dayOfWeek, isOpen, openTime, closeTime }
    const dayOfWeek = Number(row.dayOfWeek ?? row.day_of_week ?? row.day);
    if (!Number.isInteger(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6) continue;
    const openTime = String(row.openTime ?? row.open_time ?? '09:00').slice(0, 5);
    const closeTime = String(row.closeTime ?? row.close_time ?? '21:00').slice(0, 5);
    const isOpen = row.isOpen === true || row.is_open === true || row.isOpen === 'true';
    byDay.set(dayOfWeek, {
      dayOfWeek,
      isOpen,
      shifts: isOpen ? [{ open: openTime, close: closeTime }] : [],
    });
  }
  return defaultBusinessHours().map((d) => byDay.get(d.dayOfWeek) ?? d);
}

export function businessHoursToPortalFormat(hours: BusinessHourDay[]): PortalBusinessHoursEntry[] {
  return hours.map((d) => ({
    day: d.dayOfWeek,
    shifts: d.isOpen ? d.shifts : [],
  }));
}

export function storeProfileToDraft(profile: StoreProfile | null, seedSlug = ''): StoreProfileDraft {
  if (!profile) return emptyStoreProfileDraft(seedSlug);
  return {
    slug: profile.slug || seedSlug,
    displayName: profile.displayName ?? '',
    logoUrl: profile.logoUrl ?? '',
    coverStoragePath: profile.coverStoragePath ?? '',
    backgroundStoragePath: profile.backgroundStoragePath ?? '',
    storeTheme: normalizeStoreTheme(profile.storeTheme),
    description: profile.description ?? '',
    phone: profile.phone ?? '',
    whatsapp: profile.whatsapp ?? '',
    orderContactMessage: profile.orderContactMessage !== false,
    orderContactWhatsapp: profile.orderContactWhatsapp === true,
    orderContactPhone: profile.orderContactPhone === true,
    isPublic: profile.isPublic,
    deliveryEnabled: profile.deliveryEnabled,
    ownDeliveryEnabled: profile.ownDeliveryEnabled,
    vueltoRidersEnabled: profile.vueltoRidersEnabled,
    isOnDuty: profile.isOnDuty,
    businessHours: parseBusinessHours(profile.businessHours),
    manualOverride: profile.manualOverride ?? 'auto',
    manualOverrideUntil: profile.manualOverrideUntil ?? null,
    tcAcceptedAt: profile.tcAcceptedAt ?? null,
    maxConcurrentOrders: profile.maxConcurrentOrders,
    defaultPrepMinutes: profile.defaultPrepMinutes,
    autoAcceptPickupPaid: profile.autoAcceptPickupPaid === true,
    notifyCustomerReadyPickup: profile.notifyCustomerReadyPickup === true,
    minOrderAmount: profile.minOrderAmount,
    coverFocalX: profile.coverFocalX ?? 50,
    coverFocalY: profile.coverFocalY ?? 50,
    coverZoom: profile.coverZoom ?? 1,
    logoFocalX: profile.logoFocalX ?? 50,
    logoFocalY: profile.logoFocalY ?? 50,
    logoZoom: profile.logoZoom ?? 1,
    menuCartaEnabled: profile.menuCartaEnabled !== false,
  };
}

/** True si ahora cae dentro de algún tramo abierto del día (hora local del device). */
export function isStoreOpenNow(hours: BusinessHourDay[], now = new Date()): boolean {
  const day = hours.find((h) => h.dayOfWeek === now.getDay());
  if (!day?.isOpen || day.shifts.length === 0) return false;
  const toMinutes = (hhmm: string) => {
    const [h, m] = hhmm.split(':').map((n) => Number(n));
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
  };
  const current = now.getHours() * 60 + now.getMinutes();
  return day.shifts.some((shift) => {
    const open = toMinutes(shift.open);
    const close = toMinutes(shift.close);
    if (open == null || close == null) return false;
    if (close >= open) return current >= open && current < close;
    // Cruza medianoche (ej. 22:00–02:00)
    return current >= open || current < close;
  });
}

export type StoreDoorState = 'paused' | 'forced_open' | 'open' | 'closed';

/**
 * Estado efectivo de la puerta online. Un override vencido ya no aplica:
 * el portal (`evaluateStoreOpen`) cae al horario y la app muestra lo mismo.
 */
export function resolveStoreDoorState(
  draft: Pick<StoreProfileDraft, 'manualOverride' | 'manualOverrideUntil' | 'businessHours'>,
  now = new Date(),
): StoreDoorState {
  const until = draft.manualOverrideUntil ? new Date(draft.manualOverrideUntil).getTime() : null;
  const overrideActive = until == null || until > now.getTime();
  if (draft.manualOverride === 'force_close' && overrideActive) return 'paused';
  if (draft.manualOverride === 'force_open' && overrideActive) return 'forced_open';
  return isStoreOpenNow(draft.businessHours, now) ? 'open' : 'closed';
}
